import { useEffect, useRef, useState } from "react";
import TAChatAgent from "../components/TAChatAgent";

// ── Types ──────────────────────────────────────────────────────────────────
interface SignalDetail {
  label: string;
  value: string;
  bias: "bull" | "bear" | "neutral";
}

interface Opportunity {
  symbol: string;
  price: string;
  change24h: number;
  rsi: number | null;
  signal: "BUY" | "SELL" | "WATCH" | "NEUTRAL";
  reason: string;
  setupScore: number;
  signals: SignalDetail[];
}

interface NewsItem {
  title: string;
  url: string;
  source: string;
  publishedAt: string;
}

// ── Chart ──────────────────────────────────────────────────────────────────
// All available symbols
const ALL_SYMBOLS = [
  { symbol: "BTC/USDT", tv: "BINANCE:BTCUSDT" },
  { symbol: "ETH/USDT", tv: "BINANCE:ETHUSDT" },
  { symbol: "SOL/USDT", tv: "BINANCE:SOLUSDT" },
  { symbol: "BNB/USDT", tv: "BINANCE:BNBUSDT" },
  { symbol: "XRP/USDT", tv: "BINANCE:XRPUSDT" },
  { symbol: "ADA/USDT", tv: "BINANCE:ADAUSDT" },
  { symbol: "DOGE/USDT", tv: "BINANCE:DOGEUSDT" },
  { symbol: "AVAX/USDT", tv: "BINANCE:AVAXUSDT" },
  { symbol: "DOT/USDT", tv: "BINANCE:DOTUSDT" },
  { symbol: "LINK/USDT", tv: "BINANCE:LINKUSDT" },
  { symbol: "MSTR", tv: "NASDAQ:MSTR" },
  { symbol: "NVDA", tv: "NASDAQ:NVDA" },
  { symbol: "AMD", tv: "NASDAQ:AMD" },
  { symbol: "AAPL", tv: "NASDAQ:AAPL" },
  { symbol: "MSFT", tv: "NASDAQ:MSFT" },
  { symbol: "GOOGL", tv: "NASDAQ:GOOGL" },
  { symbol: "AMZN", tv: "NASDAQ:AMZN" },
  { symbol: "TSLA", tv: "NASDAQ:TSLA" },
  { symbol: "META", tv: "NASDAQ:META" },
];

// Timeframes
const TIMEFRAMES = [
  { label: "15m", value: "15" },
  { label: "1H", value: "60" },
  { label: "4H", value: "240" },
  { label: "1D", value: "D" },
  { label: "1W", value: "W" },
];

function TVChart({ symbol, interval = "240", height = 420 }: { symbol: string; interval?: string; height?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const prevSymbol = useRef("");
  const prevInterval = useRef("");

  useEffect(() => {
    if (!ref.current) return;
    // Re-render if symbol OR interval changes
    if (prevSymbol.current === symbol && prevInterval.current === interval) return;
    prevSymbol.current = symbol;
    prevInterval.current = interval;
    ref.current.innerHTML = "";

    const tvSym = ALL_SYMBOLS.find(s => s.symbol === symbol)?.tv || `BINANCE:${symbol.replace("/", "")}`;

    const init = () => {
      // @ts-ignore
      if (!window.TradingView) return;
      // @ts-ignore
      new window.TradingView.widget({
        autosize: true,
        symbol: tvSym,
        interval: interval,
        timezone: "Europe/Zurich",
        theme: "dark",
        style: "1",
        locale: "en",
        container_id: "ta-chart",
        studies: [
          "RSI@tv-basicstudies",
          "MACD@tv-basicstudies", 
          "BB@tv-basicstudies",
          "MAS@tv-basicstudies",  // Moving Average ribbon
          "VWAP@tv-basicstudies", // Volume Weighted Average Price
        ],
      });
    };

    // @ts-ignore
    if (window.TradingView) {
      init();
    } else {
      const existing = document.querySelector('script[src="https://s3.tradingview.com/tv.js"]');
      if (!existing) {
        const s = document.createElement("script");
        s.src = "https://s3.tradingview.com/tv.js";
        s.async = true;
        s.onload = init;
        document.head.appendChild(s);
      } else {
        existing.addEventListener("load", init);
      }
    }
  }, [symbol]);

  return (
    <div
      id="ta-chart"
      ref={ref}
      style={{ width: "100%", height }}
      className="bg-gray-800 rounded-xl border border-gray-700"
    />
  );
}

// ── Scanner ────────────────────────────────────────────────────────────────
const SCAN_SYMBOLS = [
  { symbol: "BTC/USDT", binance: "BTCUSDT" },
  { symbol: "ETH/USDT", binance: "ETHUSDT" },
  { symbol: "SOL/USDT", binance: "SOLUSDT" },
  { symbol: "BNB/USDT", binance: "BNBUSDT" },
];

// ── TA Math ────────────────────────────────────────────────────────────────
function calcRSI(closes: number[], period = 14): number | null {
  if (closes.length < period + 1) return null;
  const slice = closes.slice(-period - 1);
  let gains = 0, losses = 0;
  for (let i = 1; i < slice.length; i++) {
    const d = slice[i] - slice[i - 1];
    if (d >= 0) gains += d; else losses -= d;
  }
  const ag = gains / period, al = losses / period;
  if (al === 0) return 100;
  return 100 - 100 / (1 + ag / al);
}

function calcEMA(data: number[], period: number): number[] {
  const k = 2 / (period + 1);
  const ema: number[] = [data[0]];
  for (let i = 1; i < data.length; i++) ema.push(data[i] * k + ema[i - 1] * (1 - k));
  return ema;
}

function calcBB(closes: number[], period = 20): { upper: number; lower: number; mid: number; width: number } | null {
  if (closes.length < period) return null;
  const slice = closes.slice(-period);
  const mid = slice.reduce((a, b) => a + b, 0) / period;
  const std = Math.sqrt(slice.reduce((a, b) => a + (b - mid) ** 2, 0) / period);
  const upper = mid + 2 * std, lower = mid - 2 * std;
  return { upper, lower, mid, width: (upper - lower) / mid };
}

function calcStochRSI(closes: number[], rsiPeriod = 14, stochPeriod = 14): number | null {
  if (closes.length < rsiPeriod + stochPeriod + 1) return null;
  const rsiValues: number[] = [];
  for (let i = rsiPeriod; i < closes.length; i++) {
    const r = calcRSI(closes.slice(0, i + 1), rsiPeriod);
    if (r !== null) rsiValues.push(r);
  }
  if (rsiValues.length < stochPeriod) return null;
  const slice = rsiValues.slice(-stochPeriod);
  const minR = Math.min(...slice), maxR = Math.max(...slice);
  if (maxR === minR) return 50;
  return ((rsiValues[rsiValues.length - 1] - minR) / (maxR - minR)) * 100;
}

function calcATR(klines: any[][], period = 14): number | null {
  if (klines.length < period + 1) return null;
  const trs = klines.slice(-period - 1).map((k, i, arr) => {
    const high = parseFloat(k[2]), low = parseFloat(k[3]);
    const prevClose = i > 0 ? parseFloat(arr[i - 1][4]) : parseFloat(k[4]);
    return Math.max(high - low, Math.abs(high - prevClose), Math.abs(low - prevClose));
  });
  return trs.slice(1).reduce((a, b) => a + b, 0) / period;
}

function calcVolumeRatio(klines: any[][], period = 20): number | null {
  if (klines.length < period + 1) return null;
  const vols = klines.slice(-period - 1).map((k) => parseFloat(k[5]));
  const avgVol = vols.slice(0, period).reduce((a, b) => a + b, 0) / period;
  return avgVol > 0 ? vols[vols.length - 1] / avgVol : null;
}

// ADX - Average Directional Index (trend strength)
function calcADX(klines: any[][], period = 14): { adx: number; plusDI: number; minusDI: number } | null {
  if (klines.length < period * 2) return null;
  
  const highs = klines.map((k) => parseFloat(k[2]));
  const lows = klines.map((k) => parseFloat(k[3]));
  const closes = klines.map((k) => parseFloat(k[4]));
  
  const plusDM: number[] = [], minusDM: number[] = [], tr: number[] = [];
  
  for (let i = 1; i < highs.length; i++) {
    const highDiff = highs[i] - highs[i - 1];
    const lowDiff = lows[i - 1] - lows[i];
    plusDM.push(highDiff > lowDiff && highDiff > 0 ? highDiff : 0);
    minusDM.push(lowDiff > highDiff && lowDiff > 0 ? lowDiff : 0);
    const trVal = Math.max(
      highs[i] - lows[i],
      Math.abs(highs[i] - closes[i - 1]),
      Math.abs(lows[i] - closes[i - 1])
    );
    tr.push(trVal);
  }
  
  const smooth = (arr: number[], len: number) => {
    let sum = arr.slice(0, len).reduce((a, b) => a + b, 0);
    const result = [sum];
    for (let i = len; i < arr.length; i++) {
      sum = sum - sum / len + arr[i];
      result.push(sum);
    }
    return result;
  };
  
  const smoothedTR = smooth(tr, period);
  const smoothedPlusDM = smooth(plusDM, period);
  const smoothedMinusDM = smooth(minusDM, period);
  
  if (smoothedTR.length < period) return null;
  
  const plusDI: number[] = [], minusDI: number[] = [];
  for (let i = 0; i < smoothedTR.length; i++) {
    if (smoothedTR[i] === 0) { plusDI.push(0); minusDI.push(0); continue; }
    plusDI.push((smoothedPlusDM[i] / smoothedTR[i]) * 100);
    minusDI.push((smoothedMinusDM[i] / smoothedTR[i]) * 100);
  }
  
  const diDiff = plusDI.map((p, i) => Math.abs(p - minusDI[i]));
  const diSum = plusDI.map((p, i) => p + minusDI[i]);
  
  const dx = diDiff.map((d, i) => diSum[i] > 0 ? (d / diSum[i]) * 100 : 0);
  const adx = dx.slice(-period).reduce((a, b) => a + b, 0) / period;
  
  const lastIdx = plusDI.length - 1;
  return { adx, plusDI: plusDI[lastIdx], minusDI: minusDI[lastIdx] };
}

// MACD - Moving Average Convergence Divergence
function calcMACD(closes: number[], fast = 12, slow = 26, signal = 9): { macd: number; signal: number; histogram: number } | null {
  if (closes.length < slow + signal) return null;
  
  const emaFast = calcEMA(closes, fast);
  const emaSlow = calcEMA(closes, slow);
  
  const macdLine = emaFast.map((f, i) => f - emaSlow[i]);
  const signalLine = calcEMA(macdLine, signal);
  
  const lastIdx = macdLine.length - 1;
  return {
    macd: macdLine[lastIdx],
    signal: signalLine[lastIdx],
    histogram: macdLine[lastIdx] - signalLine[lastIdx]
  };
}

// Williams %R
function calcWilliamsR(klines: any[][], period = 14): number | null {
  if (klines.length < period) return null;
  const slice = klines.slice(-period);
  const highs = slice.map((k) => parseFloat(k[2]));
  const lows = slice.map((k) => parseFloat(k[3]));
  const closes = slice.map((k) => parseFloat(k[4]));
  
  const highest = Math.max(...highs);
  const lowest = Math.min(...lows);
  const current = closes[closes.length - 1];
  
  if (highest === lowest) return -50;
  return ((highest - current) / (highest - lowest)) * -100;
}

// Supertrend
function calcSupertrend(klines: any[][], period = 10, multiplier = 3): { trend: "bull" | "bear"; value: number } | null {
  if (klines.length < period) return null;
  
  const closes = klines.map((k) => parseFloat(k[4]));
  const highs = klines.map((k) => parseFloat(k[2]));
  const lows = klines.map((k) => parseFloat(k[3]));
  
  const atr = calcATR(klines, period);
  if (atr === null) return null;
  
  const upperBand: number[] = [];
  const lowerBand: number[] = [];
  
  for (let i = 0; i < closes.length; i++) {
    const mid = (highs[i] + lows[i]) / 2;
    upperBand.push(mid + multiplier * atr);
    lowerBand.push(mid - multiplier * atr);
  }
  
  let trend: "bull" | "bear" = "bull";
  for (let i = closes.length - period; i < closes.length; i++) {
    if (closes[i] > upperBand[i - 1]) trend = "bull";
    else if (closes[i] < lowerBand[i - 1]) trend = "bear";
  }
  
  return { trend, value: trend === "bull" ? lowerBand[lowerBand.length - 1] : upperBand[upperBand.length - 1] };
}

// Pivot Points (Classic)
function calcPivots(klines: any[][]): { pp: number; r1: number; r2: number; s1: number; s2: number } | null {
  if (klines.length < 1) return null;
  const last = klines[klines.length - 1];
  const high = parseFloat(last[2]);
  const low = parseFloat(last[3]);
  const close = parseFloat(last[4]);
  
  const pp = (high + low + close) / 3;
  const r1 = 2 * pp - low;
  const r2 = pp + (high - low);
  const s1 = 2 * pp - high;
  const s2 = pp - (high - low);
  
  return { pp, r1, r2, s1, s2 };
}

// OBV - On Balance Volume
function calcOBV(klines: any[]): number | null {
  if (klines.length < 2) return null;
  let obv = 0;
  for (let i = 1; i < klines.length; i++) {
    const currClose = parseFloat(klines[i][4]);
    const prevClose = parseFloat(klines[i - 1][4]);
    const volume = parseFloat(klines[i][5]);
    if (currClose > prevClose) obv += volume;
    else if (currClose < prevClose) obv -= volume;
  }
  return obv;
}

async function fetchOpportunities(): Promise<Opportunity[]> {
  return Promise.all(
    SCAN_SYMBOLS.map(async ({ symbol, binance }) => {
      try {
        const [ticker, klines, funding] = await Promise.all([
          fetch(`https://api.binance.com/api/v3/ticker/24hr?symbol=${binance}`).then(r => r.json()),
          fetch(`https://api.binance.com/api/v3/klines?symbol=${binance}&interval=4h&limit=60`).then(r => r.json()),
          fetch(`https://fapi.binance.com/fapi/v1/premiumIndex?symbol=${binance}`).then(r => r.json()).catch(() => null),
        ]);

        const price = parseFloat(ticker.lastPrice);
        const change = parseFloat(ticker.priceChangePercent);
        const closes: number[] = klines.map((k: any[]) => parseFloat(k[4]));
        const highs: number[] = klines.map((k: any[]) => parseFloat(k[2]));
        const lows: number[] = klines.map((k: any[]) => parseFloat(k[3]));

        const rsi = calcRSI(closes, 14);
        const ema9 = calcEMA(closes, 9);
        const ema21 = calcEMA(closes, 21);
        const bb = calcBB(closes, 20);
        const stochRsi = calcStochRSI(closes, 14, 14);
        const atr = calcATR(klines, 14);
        const volRatio = calcVolumeRatio(klines, 20);
        const fundingRate = funding ? parseFloat(funding.lastFundingRate) * 100 : null;
        
        // New indicators from arxiv paper
        const adx = calcADX(klines, 14);
        const macd = calcMACD(closes);
        const williamsR = calcWilliamsR(klines, 14);
        const supertrend = calcSupertrend(klines, 10, 3);
        const pivots = calcPivots(klines);
        const obv = calcOBV(klines);

        const ema9Last = ema9[ema9.length - 1], ema21Last = ema21[ema21.length - 1];
        const ema9Prev = ema9[ema9.length - 2], ema21Prev = ema21[ema21.length - 2];
        const emaCross = ema9Last > ema21Last ? "bull" : "bear";
        const emaCrossed = (ema9Last > ema21Last && ema9Prev <= ema21Prev) ? "golden"
          : (ema9Last < ema21Last && ema9Prev >= ema21Prev) ? "death" : null;

        const bbSqueeze = bb ? bb.width < 0.04 : false;
        const bbPos = bb ? (price - bb.lower) / (bb.upper - bb.lower) : 0.5;

        const recentHighs = highs.slice(-5), recentLows = lows.slice(-5);
        const hhhl = recentHighs[4] > recentHighs[0] && recentLows[4] > recentLows[0];
        const lhll = recentHighs[4] < recentHighs[0] && recentLows[4] < recentLows[0];

        const signals: SignalDetail[] = [];

        if (rsi !== null) signals.push({
          label: "RSI (14)", value: rsi.toFixed(1),
          bias: rsi <= 35 ? "bull" : rsi >= 65 ? "bear" : "neutral",
        });
        signals.push({
          label: "EMA 9/21",
          value: emaCrossed === "golden" ? "Golden Cross ✨" : emaCrossed === "death" ? "Death Cross 💀"
            : emaCross === "bull" ? "Bullish (9>21)" : "Bearish (9<21)",
          bias: emaCross === "bull" ? "bull" : "bear",
        });
        if (bb) signals.push({
          label: "Bollinger Bands",
          value: bbSqueeze ? "Squeeze 🔥" : bbPos > 0.8 ? "Near upper" : bbPos < 0.2 ? "Near lower" : "Mid range",
          bias: bbSqueeze ? "neutral" : bbPos < 0.2 ? "bull" : bbPos > 0.8 ? "bear" : "neutral",
        });
        if (stochRsi !== null) signals.push({
          label: "Stoch RSI", value: stochRsi.toFixed(1),
          bias: stochRsi <= 20 ? "bull" : stochRsi >= 80 ? "bear" : "neutral",
        });
        if (volRatio !== null) signals.push({
          label: "Volume", value: `${volRatio.toFixed(2)}x avg`,
          bias: volRatio >= 1.5 ? (change >= 0 ? "bull" : "bear") : "neutral",
        });
        if (atr !== null) signals.push({
          label: "ATR (14)",
          value: price >= 1000 ? `$${atr.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : `$${atr.toFixed(4)}`,
          bias: "neutral",
        });
        signals.push({
          label: "Trend Structure",
          value: hhhl ? "HH/HL — Uptrend" : lhll ? "LH/LL — Downtrend" : "Choppy",
          bias: hhhl ? "bull" : lhll ? "bear" : "neutral",
        });
        if (fundingRate !== null) signals.push({
          label: "Funding Rate",
          value: `${fundingRate >= 0 ? "+" : ""}${fundingRate.toFixed(4)}%`,
          bias: fundingRate > 0.05 ? "bear" : fundingRate < -0.01 ? "bull" : "neutral",
        });
        
        // ADX - trend strength
        if (adx) signals.push({
          label: "ADX (14)",
          value: adx.adx.toFixed(1),
          bias: adx.adx >= 25 ? (adx.plusDI > adx.minusDI ? "bull" : "bear") : "neutral",
        });
        
        // MACD
        if (macd) signals.push({
          label: "MACD",
          value: macd.histogram >= 0 ? `▲ ${macd.histogram.toFixed(2)}` : `▼ ${Math.abs(macd.histogram).toFixed(2)}`,
          bias: macd.histogram > 0 ? "bull" : macd.histogram < 0 ? "bear" : "neutral",
        });
        
        // Williams %R
        if (williamsR !== null) signals.push({
          label: "Williams %R",
          value: williamsR.toFixed(1),
          bias: williamsR <= -80 ? "bull" : williamsR >= -20 ? "bear" : "neutral",
        });
        
        // Supertrend
        if (supertrend) signals.push({
          label: "Supertrend",
          value: supertrend.trend === "bull" ? "▲ Uptrend" : "▼ Downtrend",
          bias: supertrend.trend,
        });
        
        // Pivot Points
        if (pivots) signals.push({
          label: "Pivots",
          value: `R1 ${pivots.r1.toFixed(0)} | S1 ${pivots.s1.toFixed(0)}`,
          bias: "neutral",
        });
        
        // OBV
        if (obv !== null) signals.push({
          label: "OBV",
          value: obv >= 1e9 ? `${(obv / 1e9).toFixed(2)}B` : obv >= 1e6 ? `${(obv / 1e6).toFixed(2)}M` : obv.toFixed(0),
          bias: "neutral",
        });

        // Enhanced scoring with new indicators
        const bulls = signals.filter(s => s.bias === "bull").length;
        const total = signals.filter(s => s.bias !== "neutral").length || 1;
        
        // ADX trend strength bonus
        let scoreBonus = 0;
        if (adx && adx.adx >= 25) {
          scoreBonus = adx.plusDI > adx.minusDI ? 1 : -1;
        }
        
        // Supertrend alignment bonus
        if (supertrend) {
          if (supertrend.trend === "bull" && emaCross === "bull") scoreBonus += 1;
          if (supertrend.trend === "bear" && emaCross === "bear") scoreBonus -= 1;
        }
        
        // MACD momentum
        if (macd && macd.histogram > 0 && emaCross === "bull") scoreBonus += 0.5;
        if (macd && macd.histogram < 0 && emaCross === "bear") scoreBonus -= 0.5;
        
        const baseScore = (bulls / total) * 10;
        const setupScore = Math.max(0, Math.min(10, Math.round(baseScore + scoreBonus)));

        let signal: Opportunity["signal"] = "NEUTRAL";
        const reasons: string[] = [];

        if (rsi !== null && rsi <= 30) { signal = "BUY"; reasons.push(`RSI oversold (${rsi.toFixed(1)})`); }
        else if (rsi !== null && rsi >= 70) { signal = "SELL"; reasons.push(`RSI overbought (${rsi.toFixed(1)})`); }
        if (emaCrossed === "golden") { signal = "BUY"; reasons.push("EMA golden cross"); }
        else if (emaCrossed === "death") { signal = "SELL"; reasons.push("EMA death cross"); }
        if (bbSqueeze) reasons.push("BB squeeze — breakout imminent");
        if (volRatio !== null && volRatio >= 2) reasons.push(`Volume spike ${volRatio.toFixed(1)}x`);
        if (stochRsi !== null && stochRsi <= 20 && signal === "NEUTRAL") { signal = "WATCH"; reasons.push("StochRSI oversold"); }
        if (stochRsi !== null && stochRsi >= 80 && signal === "NEUTRAL") { signal = "WATCH"; reasons.push("StochRSI overbought"); }
        if (setupScore >= 7 && signal === "NEUTRAL") signal = "WATCH";
        if (setupScore <= 3 && signal === "NEUTRAL") signal = "WATCH";
        if (reasons.length === 0) reasons.push(emaCross === "bull" ? "Bullish trend structure" : "Bearish trend structure");

        return {
          symbol,
          price: price >= 1000 ? `$${price.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : `$${price.toFixed(3)}`,
          change24h: change, rsi, signal,
          reason: reasons.join(" · "),
          setupScore, signals,
        };
      } catch {
        return { symbol, price: "—", change24h: 0, rsi: null, signal: "NEUTRAL" as const, reason: "Data unavailable", setupScore: 5, signals: [] };
      }
    })
  );
}


// ── News ───────────────────────────────────────────────────────────────────
async function fetchNews(): Promise<NewsItem[]> {
  try {
    const res = await fetch(
      "https://min-api.cryptocompare.com/data/v2/news/?lang=EN&categories=BTC,ETH,Trading&excludeCategories=Sponsored"
    );
    const json = await res.json();
    return (json.Data ?? []).slice(0, 8).map((item: any) => ({
      title: item.title,
      url: item.url,
      source: item.source_info?.name ?? item.source,
      publishedAt: new Date(item.published_on * 1000).toLocaleString(),
    }));
  } catch {
    return [];
  }
}

// ── Signal badge ───────────────────────────────────────────────────────────
function SignalBadge({ signal }: { signal: Opportunity["signal"] }) {
  const map = {
    BUY:     "bg-green-900/50 text-green-400 border-green-700",
    SELL:    "bg-red-900/50 text-red-400 border-red-700",
    WATCH:   "bg-yellow-900/50 text-yellow-400 border-yellow-700",
    NEUTRAL: "bg-gray-700 text-gray-400 border-gray-600",
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-bold border ${map[signal]}`}>
      {signal}
    </span>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────
export default function TechnicalAnalysis() {
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [news, setNews] = useState<NewsItem[]>([]);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [selectedSymbol, setSelectedSymbol] = useState("BTC/USDT");
  const [selectedInterval, setSelectedInterval] = useState("240");

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4 md:p-6 max-w-7xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold">📊 Technical Analysis</h1>
          <p className="text-gray-400 text-sm mt-1">Charts · Indicators · Patterns</p>
        </div>
      </div>

      {/* TA Guide */}
      <details className="mb-6 bg-gray-800 rounded-xl border border-gray-700 p-4">
        <summary className="cursor-pointer text-lg font-semibold text-blue-400 hover:text-blue-300">
          📖 How to Read This TA Scanner
        </summary>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <h4 className="font-semibold text-green-400 mb-2">🟢 Bullish Signs</h4>
            <ul className="space-y-1 text-gray-300">
              <li><strong>RSI &lt; 30</strong> — Oversold, potential bounce</li>
              <li><strong>Golden Cross</strong> — EMA 9 crosses above EMA 21</li>
              <li><strong>Stoch RSI &lt; 20</strong> — Oversold momentum</li>
              <li><strong>BB Squeeze</strong> — Low volatility, breakout imminent</li>
              <li><strong>ADX &gt; 25 + +DI &gt; -DI</strong> — Strong uptrend</li>
              <li><strong>MACD Histogram &gt; 0</strong> — Bullish momentum</li>
              <li><strong>Williams %R &lt; -80</strong> — Oversold</li>
              <li><strong>Supertrend = Uptrend</strong> — Price above trend line</li>
              <li><strong>Funding Rate &lt; 0</strong> — Shorts paying longs</li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-red-400 mb-2">🔴 Bearish Signs</h4>
            <ul className="space-y-1 text-gray-300">
              <li><strong>RSI &gt; 70</strong> — Overbought, potential pullback</li>
              <li><strong>Death Cross</strong> — EMA 9 crosses below EMA 21</li>
              <li><strong>Stoch RSI &gt; 80</strong> — Overbought momentum</li>
              <li><strong>Price near BB Upper</strong> — Extended, may revert</li>
              <li><strong>ADX &gt; 25 + -DI &gt; +DI</strong> — Strong downtrend</li>
              <li><strong>MACD Histogram &lt; 0</strong> — Bearish momentum</li>
              <li><strong>Williams %R &gt; -20</strong> — Overbought</li>
              <li><strong>Supertrend = Downtrend</strong> — Price below trend line</li>
              <li><strong>Funding Rate &gt; 0.05%</strong> — Longs paying shorts</li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-yellow-400 mb-2">📊 Key Indicators Explained</h4>
            <ul className="space-y-1 text-gray-300">
              <li><strong>RSI (14)</strong> — Momentum 0-100. &lt;30 oversold, &gt;70 overbought</li>
              <li><strong>EMA 9/21</strong> — Exponential moving averages. Short vs long term trend</li>
              <li><strong>Bollinger Bands</strong> — Price envelope. Squeeze = low volatility</li>
              <li><strong>Stoch RSI</strong> — RSI's momentum. &lt;20 oversold, &gt;80 overbought</li>
              <li><strong>ATR (14)</strong> — Average True Range. Measures volatility</li>
              <li><strong>ADX</strong> — Trend strength. &gt;25 = strong trend</li>
              <li><strong>MACD</strong> — Trend/momentum. Signal line crossovers</li>
              <li><strong>Williams %R</strong> — Like RSI but inverted. &lt;-80 oversold</li>
              <li><strong>Supertrend</strong> — Trend direction based on ATR</li>
              <li><strong>OBV</strong> — On Balance Volume. Confirms price moves</li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-blue-400 mb-2">🎯 Scoring System</h4>
            <ul className="space-y-1 text-gray-300">
              <li><strong>7-10/10</strong> — Strong bullish setup</li>
              <li><strong>4-6/10</strong> — Neutral, wait for confirmation</li>
              <li><strong>0-3/10</strong> — Weak/bearish setup</li>
              <li><strong>BUY Signal</strong> — RSI oversold OR golden cross</li>
              <li><strong>SELL Signal</strong> — RSI overbought OR death cross</li>
              <li><strong>WATCH</strong> — Extreme readings, high volatility</li>
            </ul>
            <h4 className="font-semibold text-purple-400 mt-4 mb-2">🏗️ Pattern Recognition</h4>
            <ul className="space-y-1 text-gray-300">
              <li><strong>HH/HL</strong> — Higher highs/lowers = Uptrend</li>
              <li><strong>LH/LL</strong> — Lower highs/lowers = Downtrend</li>
              <li><strong>BB Squeeze</strong> — Consolidation, explosive move coming</li>
              <li><strong>Volume Spike</strong> — &gt;1.5x avg = institutional interest</li>
            </ul>
          </div>
        </div>
      </details>

      {/* Symbol tabs */}
      <div className="flex gap-2 flex-wrap mb-4">
        {opportunities.map((opp) => (
          <button
            key={opp.symbol}
            onClick={() => setSelectedSymbol(opp.symbol)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              selectedSymbol === opp.symbol
                ? "bg-blue-600 text-white"
                : "bg-gray-700 text-gray-300 hover:bg-gray-600"
            }`}
          >
            {opp.symbol}
          </button>
        ))}
      </div>

      {/* Symbol tabs */}
      <div className="flex gap-2 flex-wrap mb-4">
        {ALL_SYMBOLS.map((s) => (
          <button
            key={s.symbol}
            onClick={() => setSelectedSymbol(s.symbol)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              selectedSymbol === s.symbol
                ? "bg-blue-600 text-white"
                : "bg-gray-700 text-gray-300 hover:bg-gray-600"
            }`}
          >
            {s.symbol}
          </button>
        ))}
      </div>

      {/* Timeframe selector */}
      <div className="flex gap-2 mb-4">
        <span className="text-gray-400 text-sm self-center mr-2">Timeframe:</span>
        {TIMEFRAMES.map((tf) => (
          <button
            key={tf.value}
            onClick={() => setSelectedInterval(tf.value)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              selectedInterval === tf.value
                ? "bg-green-600 text-white"
                : "bg-gray-700 text-gray-300 hover:bg-gray-600"
            }`}
          >
            {tf.label}
          </button>
        ))}
      </div>

      {/* TradingView chart - at top */}
      <TVChart symbol={selectedSymbol} interval={selectedInterval} height={450} />
    </div>
  );
}
